<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'MasterData',
  },
})

const ability = useAbility()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})

const formDefault = () => ({ name: '', code: '', severity_level: null, description: '', is_active: true })

const severityOptions = [
  { title: '1 - Paling ringan', value: 1 },
  { title: '2', value: 2 },
  { title: '3', value: 3 },
  { title: '4', value: 4 },
  { title: '5 - Paling berat', value: 5 },
]

const form = ref(formDefault())

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/incident-types', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = data?.meta?.total ?? data?.total ?? 0
  } catch (e) {
    console.error('Fetch incident types error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  dialogTitle.value = 'Tambah Jenis Insiden'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = { name: item.name, code: item.code, severity_level: item.severity_level, description: item.description ?? '', is_active: item.is_active }
  errors.value = {}
  dialogTitle.value = 'Edit Jenis Insiden'
  dialog.value = true
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const api = useApi(editingId.value ? `/api/v1/master-data/incident-types/${editingId.value}` : '/api/v1/master-data/incident-types')
    const response = editingId.value ? await api.put({ body: form.value }) : await api.post({ body: form.value })
    const saved = response?.data ?? response

    if (editingId.value) {
      const idx = items.value.findIndex(i => i.id === editingId.value)
      if (idx !== -1) items.value[idx] = saved
    } else {
      fetchItems()
    }
    dialog.value = false
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save incident type error:', e)
  } finally {
    saving.value = false
  }
}

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/master-data/incident-types/${deletingItem.value.id}`).delete()
    deleteDialog.value = false
    fetchItems()
  } catch (e) {
    console.error('Delete incident type error:', e)
  } finally {
    deleting.value = false
  }
}

const headers = [
  { title: 'Kode', key: 'code', width: 120 },
  { title: 'Nama', key: 'name' },
  { title: 'Level Keparahan', key: 'severity_level', align: 'center', width: 140 },
  { title: 'Status', key: 'is_active', align: 'center', width: 100 },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 120 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Jenis Insiden
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Master data jenis insiden keselamatan pasien (Near Miss, KTD, KNC, KPC, Sentinel)
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Jenis Insiden
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari nama atau kode..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <template #item.code="{ item }">
          <code>{{ item.code }}</code>
        </template>
        <template #item.severity_level="{ item }">
          <VChip
            v-if="item.severity_level"
            size="small"
            :color="item.severity_level >= 4 ? 'error' : (item.severity_level >= 2 ? 'warning' : 'secondary')"
          >
            {{ item.severity_level }}
          </VChip>
          <span v-else>-</span>
        </template>
        <template #item.is_active="{ item }">
          <VChip
            size="small"
            :color="item.is_active ? 'success' : 'secondary'"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="520"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama *"
                :error-messages="errors.name"
                autofocus
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.code"
                label="Kode *"
                :error-messages="errors.code"
              />
            </VCol>
            <VCol cols="12">
              <VSelect
                v-model="form.severity_level"
                :items="severityOptions"
                label="Level Keparahan"
                clearable
                :error-messages="errors.severity_level"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.description"
                label="Deskripsi"
                rows="2"
                :error-messages="errors.description"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Jenis Insiden?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.name }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
